import React from 'react'
import numeral from 'numeral'
import { useTranslation } from 'react-i18next'
import { Table, Thead, Tbody, Tr, Th, Td, Heading } from '@chakra-ui/react'
import { Box, Flex, Spacer } from '@chakra-ui/layout'
import { Doughnut } from 'react-chartjs-2'
import type { ChartOptions } from 'chart.js'
import DoughnutColuredLabel from '../DoughnutColuredLabel'
import type {
  BandwidthAndRequest,
  BasicPieChartData,
  AnalyticsAPIResponse,
} from '../../../interfaces'

const pieBackgrounds = [
  '#FB8C00',
  '#80DEEA',
  '#5E35B1',
  '#4CAF50',
  '#FF8A80',
  '#B0BEC5',
  '#795548',
  '#BF360C',
  '#C6FF00',
  '#006064',
  '#DCEDC8',
  '#B2EBF2',
  '#5C6BC0',
  '#6A1B9A',
  '#B71C1C',
  '#448AFF',
  '#64FFDA',
  '#AFB42B',
  '#FFD54F',
  '#FF6E40',
]

const getPieChartData = (
  data: BandwidthAndRequest | undefined,
): BasicPieChartData | null => {
  if (!data || !data.pattern || !data.pattern.length) {
    return null
  }

  const labels: string[] = []
  const dataset: number[] = []
  for (const dataPatter of data.pattern) {
    if (dataPatter.count && data.total) {
      dataset.push(
        parseFloat(((dataPatter.count / data.total) * 100).toFixed(2)),
      )
      labels.push(dataPatter.name)
    }
  }
  return {
    labels,
    datasets: [
      {
        backgroundColor: pieBackgrounds,
        data: dataset,
      },
    ],
  }
}

const getPieChartOptions = (): ChartOptions<'doughnut'> => ({
  animation: {
    duration: 0,
  },
  responsive: true,
  radius: 125,
  plugins: {
    legend: {
      display: false,
    },
  },
})

interface Props {
  type: 'bandwidth' | 'request'
  data: AnalyticsAPIResponse
}

const Stats = (props: Props): React.ReactElement => {
  const { data, type } = props
  const { t } = useTranslation()
  const pieChartOptions = getPieChartOptions()

  const bandwidthData: BandwidthAndRequest | undefined = data.bandwidth
  const requestData: BandwidthAndRequest | undefined = data.request

  const bandwidthTableRows: React.ReactElement[] = []
  const requestTableRows: React.ReactElement[] = []

  const pieChartBandwidthData = getPieChartData(bandwidthData)

  if (
    pieChartBandwidthData?.labels.length &&
    pieChartBandwidthData.datasets.length &&
    bandwidthData
  ) {
    pieChartBandwidthData.labels.forEach((label, i) => {
      let value = ''
      if (i < pieChartBandwidthData.datasets[0].data.length - 1) {
        if (bandwidthData.pattern) {
          value = numeral(bandwidthData.pattern[i].count)
            .format('0.0 ib')
            .replace('i', '')
        }
      } else {
        let total = 0
        if (bandwidthData.pattern) {
          bandwidthData.pattern.slice(i).forEach(el => {
            total += Number(el.count)
          })
        }
        value = numeral(total).format('0.0 ib').replace('i', '')
      }
      bandwidthTableRows.push(
        <Tr key={label}>
          <Td>
            <DoughnutColuredLabel
              label={pieChartBandwidthData.labels[i]}
              color={pieChartBandwidthData.datasets[0].backgroundColor[i]}
            />
          </Td>
          <Td>{value.toUpperCase()}</Td>
          <Td>{`${pieChartBandwidthData.datasets[0].data[i]}%`}</Td>
        </Tr>,
      )
    })
  }

  const pieChartRequestData = getPieChartData(requestData)

  if (
    pieChartRequestData?.labels.length &&
    pieChartRequestData.datasets.length &&
    requestData
  ) {
    pieChartRequestData.labels.forEach((label, i) => {
      let value = ''
      if (i < pieChartRequestData.datasets[0].data.length - 1) {
        if (requestData.pattern) {
          value = numeral(requestData.pattern[i].count).format('0.0 a')
        }
      } else {
        let total = 0
        if (requestData.pattern) {
          requestData.pattern.slice(i).forEach(el => {
            total += Number(el.count)
          })
        }
        value = numeral(total).format('0.0 a')
      }
      requestTableRows.push(
        <Tr key={label}>
          <Td>
            <DoughnutColuredLabel
              label={pieChartRequestData.labels[i]}
              color={pieChartRequestData.datasets[0].backgroundColor[i]}
            />
          </Td>
          <Td>{value.toUpperCase()}</Td>
          <Td>{`${pieChartRequestData.datasets[0].data[i]}%`}</Td>
        </Tr>,
      )
    })
  }

  return (
    <Box marginTop="2.5rem">
      <Flex>
        <Box>
          {type === 'bandwidth' &&
            bandwidthData &&
            bandwidthData.pattern &&
            bandwidthData.pattern.length && (
              <React.Fragment>
                <Flex direction="column" alignItems="center">
                  <Box>
                    <Heading as="h4" size="md">
                      {numeral(bandwidthData.total)
                        .format('0.0ib')
                        .replace('i', '')}
                    </Heading>
                  </Box>
                  <Box>
                    {t(
                      'total_bandwidth_delivered_from_imageKit',
                      'Total bandwidth delivered from ImageKit',
                    )}
                  </Box>
                </Flex>
                {pieChartBandwidthData && (
                  <Doughnut
                    data={pieChartBandwidthData}
                    options={pieChartOptions}
                  />
                )}
              </React.Fragment>
            )}
          {type === 'request' &&
            requestData &&
            requestData.pattern &&
            requestData.pattern.length && (
              <React.Fragment>
                <Flex direction="column" alignItems="center">
                  <Box>
                    <Heading as="h4" size="md">
                      {numeral(requestData.total).format('0.00 a')}
                    </Heading>
                  </Box>
                  <Box>
                    {t(
                      'total_requests_delivered_from_imageKit',
                      'Total requests delivered from ImageKit',
                    )}
                  </Box>
                </Flex>
                {pieChartRequestData && (
                  <Doughnut
                    data={pieChartRequestData}
                    options={pieChartOptions}
                  />
                )}
              </React.Fragment>
            )}
        </Box>
        <Spacer />
        <Box>
          <Table>
            <Thead>
              <Tr>
                <Th>{t('url_identifier', 'URL identifier')}</Th>
                <Th>{type === 'bandwidth' ? 'Bandwidth' : 'Count'}</Th>
                <Th>{t('percentage_of_total', 'Percentage of total')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {type === 'request' ? requestTableRows : bandwidthTableRows}
            </Tbody>
          </Table>
        </Box>
      </Flex>
    </Box>
  )
}

export default Stats
